import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useAppDispatch } from '../../../store/redux/hooks';
import { useHighlightSection } from '../../../hooks/useHighlightSection';
import {
  setFieldValue,
  setExtId,
  setGroupTags,
  setBarcode,
  setNfcEnabled,
  setNfcSource,
  setNfcCustomValue,
  setExpiryDate,
  setExpiryTime,
  setExpiryTimezone,
  setTemplateUuid,
  setFieldLocalisation,
  setPassFeaturedAction,
  clearPassFeaturedAction,
  swapPassFeaturedActions,
} from '../../../store/redux/slices/pass.slice';
import type { PassBarcode } from '../../../store/types/pass';
import PassOverviewSection from './sections/PassOverviewSection';
import DistributionLinkSection from './sections/DistributionLinkSection';
import GroupTagsSection from './sections/GroupTagsSection';
import PassManagementSection from './sections/PassManagementSection';
import SendTestNotificationSection from './sections/SendTestNotificationSection';
import ScanningSection from './sections/ScanningSection';
import FrontFieldsSection from './sections/FrontFieldsSection';
import BackFieldsSection from './sections/BackFieldsSection';
import PassExpirySection from './sections/PassExpirySection';
import FeaturedActionsSection from './sections/FeaturedActionsSection';
import StampPassSection from './sections/StampPassSection';
import { usePassBuilderData } from './hooks/usePassBuilderData';
import { usePassLocalisation } from './hooks/usePassLocalisation';
import { usePassFieldHighlight } from './hooks/usePassFieldHighlight';

interface PassBuilderPanelProps {
  isEdit?: boolean;
  isCreate?: boolean;
  errors?: Record<string, string>;
  setFieldError?: (path: string, message: string | null) => void;
  onActiveSection?: (section: 'front' | 'back', locale?: string | null) => void;
  onFieldFocus?: (fieldIdentifier: string | null) => void;
  onClearErrors?: () => void;
  focusedField?: string | null;
}

const PassBuilderPanel = ({
  isEdit,
  isCreate,
  errors = {},
  setFieldError,
  onActiveSection,
  onFieldFocus,
  onClearErrors,
  focusedField,
}: PassBuilderPanelProps) => {
  const dispatch = useAppDispatch();
  const isViewMode = !isEdit && !isCreate;

  const {
    uuid,
    pass,
    passState,
    template,
    templateAttrs,
    templateFields,
    passContent,
    nfcRaw,
    localisationEnabled,
    distributionUrl,
    groupTags,
    isStampCard,
    totalStamps,
    currentStamps,
    templateEntityUuid,
    nfcEntityEnabled,
    hasNfcEnabled: passHasNfc,
    templateType,
    enhancedDesignType,
  } = usePassBuilderData(isCreate);
  const showFeaturedActions = enhancedDesignType === 'generic';

  const {
    fieldSlots,
    hasLocalisation,
    fieldsWithLocalisation,
    templateLanguages,
    expandedTranslations,
    handleAddTranslations,
    handleRemoveTranslations,
  } = usePassLocalisation(
    template,
    templateFields,
    localisationEnabled,
    passState.fieldLocalisations,
    errors
  );

  const { focusedFieldId, focusedLocale, handleFieldSectionFocus } =
    usePassFieldHighlight(fieldSlots, focusedField);

  const walletSectionRef = useRef<HTMLDivElement>(null);
  const notificationSectionRef = useRef<HTMLDivElement>(null);
  useHighlightSection({
    matchValue: 'wallet',
    getElement: () => walletSectionRef.current,
    enabled: isViewMode,
  });
  useHighlightSection({
    matchValue: 'notification',
    getElement: () => notificationSectionRef.current,
    enabled: isViewMode,
  });

  // Clamp NFC to disabled when entity doesn't have NFC entitlement
  useEffect(() => {
    if (!nfcEntityEnabled && passState.nfcEnabled) {
      dispatch(setNfcEnabled(false));
    }
  }, [nfcEntityEnabled, passState.nfcEnabled, dispatch]);

  const handleFieldChange = (
    fieldId: string,
    value: string,
    fieldList: ({ id: string; label: string } | undefined)[]
  ) => {
    dispatch(setFieldValue({ fieldId, value }));
    const anchorHrefMatch = value.match(/<a\s+href=['"](.*?)['"]/);
    if (!value.trim()) {
      const field = fieldList.find((f) => f?.id === fieldId);
      setFieldError?.(
        `fieldValues.${fieldId}`,
        `${field?.label || fieldId} is required`
      );
    } else if (
      anchorHrefMatch &&
      anchorHrefMatch[1] &&
      !/^(https?:\/\/|mailto:|tel:)/i.test(anchorHrefMatch[1])
    ) {
      const field = fieldList.find((f) => f?.id === fieldId);
      setFieldError?.(
        `fieldValues.${fieldId}`,
        `${field?.label || fieldId}: URL must start with https://, http://, mailto: or tel:`
      );
    } else {
      setFieldError?.(`fieldValues.${fieldId}`, null);
    }
  };

  // ---- Render ----

  return (
    <Box
      border="2px solid lightGrey"
      borderRadius="10px"
      p={8}
      h="full"
      display="flex"
      flexDirection="column"
      gap={8}
      onFocusCapture={() => {
        onActiveSection?.('front', null);
        onFieldFocus?.(null);
      }}
    >
      <PassOverviewSection
        uuid={uuid}
        attributes={pass?.attributes}
        template={template}
        enhancedDesignType={enhancedDesignType}
        isEdit={isEdit}
        isCreate={isCreate}
        extIdRequired={
          (passState.barcode.enabled && passState.barcode.source === 'extId') ||
          (passState.nfcEnabled && passState.nfcSource === 'extId')
        }
        errors={errors}
        focusedField={focusedField}
        onFieldFocus={onFieldFocus}
        onSetExtId={(v) => {
          dispatch(setExtId(v));
          const extIdRequired =
            (passState.barcode.enabled &&
              passState.barcode.source === 'extId') ||
            (passState.nfcEnabled && passState.nfcSource === 'extId');
          if (extIdRequired && !v.trim()) {
            setFieldError?.(
              'extId',
              'External ID is required when used as barcode or NFC source'
            );
          } else {
            setFieldError?.('extId', null);
          }
        }}
        onTemplateChange={(v) => {
          dispatch(setTemplateUuid(v));
          onClearErrors?.();
        }}
      />

      {/* Edit/Create sections */}
      {(isEdit || isCreate) && (
        <Box
          opacity={isCreate && !template ? 0.5 : 1}
          pointerEvents={isCreate && !template ? 'none' : 'auto'}
          display="flex"
          flexDirection="column"
          gap={8}
        >
          <FrontFieldsSection
            template={template}
            passContent={passContent}
            isEdit={isEdit}
            fieldValues={passState.fieldValues}
            focusedFieldId={focusedFieldId}
            focusedLocale={focusedLocale}
            errors={errors}
            loyaltyBalance={templateAttrs?.loyaltyBalance}
            hasLocalisation={hasLocalisation}
            fieldsWithLocalisation={fieldsWithLocalisation}
            templateLanguages={templateLanguages}
            fieldLocalisations={passState.fieldLocalisations}
            expandedTranslations={expandedTranslations}
            onLocalisationChange={(fieldId, locale, value) =>
              dispatch(setFieldLocalisation({ fieldId, locale, value }))
            }
            onAddTranslations={handleAddTranslations}
            onRemoveTranslations={handleRemoveTranslations}
            onFocus={handleFieldSectionFocus(
              'front',
              onActiveSection,
              onFieldFocus
            )}
            onFieldChange={(fieldId, value) =>
              handleFieldChange(fieldId, value, [
                template?.header?.header_one,
                template?.header?.header_two,
                template?.header?.header_three,
                template?.primary?.primary_one,
                template?.primary?.primary_two,
                template?.secondary?.sec_one,
                template?.secondary?.sec_two,
                template?.secondary?.sec_three,
                template?.auxiliary?.aux_one,
                template?.auxiliary?.aux_two,
                template?.auxiliary?.aux_three,
              ])
            }
          />
          <BackFieldsSection
            template={template}
            passContent={passContent}
            isEdit={isEdit}
            fieldValues={passState.fieldValues}
            errors={errors}
            focusedFieldId={focusedFieldId}
            focusedLocale={focusedLocale}
            hasLocalisation={hasLocalisation}
            fieldsWithLocalisation={fieldsWithLocalisation}
            templateLanguages={templateLanguages}
            fieldLocalisations={passState.fieldLocalisations}
            expandedTranslations={expandedTranslations}
            onLocalisationChange={(fieldId, locale, value) =>
              dispatch(setFieldLocalisation({ fieldId, locale, value }))
            }
            onAddTranslations={handleAddTranslations}
            onRemoveTranslations={handleRemoveTranslations}
            onFocus={handleFieldSectionFocus(
              'back',
              onActiveSection,
              onFieldFocus
            )}
            onFieldChange={(fieldId, value) =>
              handleFieldChange(fieldId, value, [
                template?.back_fields?.back_one,
                template?.back_fields?.back_two,
                template?.back_fields?.back_three,
                template?.back_fields?.back_four,
                template?.back_fields?.back_five,
              ])
            }
          />
          {showFeaturedActions && (
            <FeaturedActionsSection
              value={passState.featuredActions}
              errors={errors}
              setFieldError={setFieldError}
              onFieldFocus={onFieldFocus}
              onChange={(slot, value) =>
                dispatch(setPassFeaturedAction({ slot, value }))
              }
              onRemove={(slot) => dispatch(clearPassFeaturedAction(slot))}
              onSwap={() => dispatch(swapPassFeaturedActions())}
            />
          )}
          <PassExpirySection
            expiryDate={passState.expiryDate}
            expiryTime={passState.expiryTime}
            expiryTimezone={passState.expiryTimezone}
            isCreate={isCreate}
            hasExistingExpiry={!!pass?.attributes?.expiresAt}
            errors={errors}
            onFieldFocus={onFieldFocus}
            onExpiryDateChange={(v) => dispatch(setExpiryDate(v))}
            onExpiryTimeChange={(v) => {
              dispatch(setExpiryTime(v));
              if (v) setFieldError?.('expiryTime', null);
            }}
            onExpiryTimezoneChange={(v) => dispatch(setExpiryTimezone(v))}
          />
          <ScanningSection
            isEdit
            barcode={passState.barcode}
            nfcEnabled={passState.nfcEnabled}
            nfcSource={passState.nfcSource}
            onFieldFocus={onFieldFocus}
            focusedField={focusedField}
            nfcCustomValue={passState.nfcCustomValue}
            nfcEntityEnabled={nfcEntityEnabled}
            errors={errors}
            onBarcodeChange={(changes) => {
              if ('enabled' in changes && !changes.enabled) {
                dispatch(
                  setBarcode({
                    enabled: false,
                    type: null,
                    source: null,
                    value: '',
                    displayText: false,
                  })
                );
                setFieldError?.('barcode.type', null);
                setFieldError?.('barcode.source', null);
                setFieldError?.('barcode.value', null);
                return;
              }
              if ('enabled' in changes && changes.enabled) {
                const tb = templateAttrs?.barcode;
                dispatch(
                  setBarcode({
                    enabled: true,
                    type: (tb?.type as PassBarcode['type']) ?? 'qr',
                    source: (tb?.source as PassBarcode['source']) ?? 'uuid',
                    value: tb?.source === 'custom' ? (tb?.value ?? '') : '',
                    displayText: tb?.displayText ?? false,
                  })
                );
                return;
              }
              dispatch(setBarcode(changes));
              if ('type' in changes && changes.type)
                setFieldError?.('barcode.type', null);
              if ('source' in changes && changes.source)
                setFieldError?.('barcode.source', null);
              if ('value' in changes) {
                if (changes.value?.trim()) {
                  setFieldError?.('barcode.value', null);
                } else if (passState.barcode.source === 'custom') {
                  setFieldError?.('barcode.value', 'Barcode value is required');
                }
              }
            }}
            onNfcEnabledChange={(v) => {
              dispatch(setNfcEnabled(v));
              if (!v) {
                dispatch(setNfcSource('uuid'));
                dispatch(setNfcCustomValue(''));
                setFieldError?.('nfcCustomValue', null);
              } else {
                const tn = templateAttrs?.nfc;
                dispatch(setNfcSource(tn?.source ?? 'uuid'));
                dispatch(
                  setNfcCustomValue(
                    tn?.source === 'custom' ? (tn?.customValue ?? '') : ''
                  )
                );
              }
            }}
            onNfcSourceChange={(v) => dispatch(setNfcSource(v))}
            onNfcCustomValueChange={(v) => {
              dispatch(setNfcCustomValue(v));
              if (v.trim()) {
                setFieldError?.('nfcCustomValue', null);
              } else {
                setFieldError?.('nfcCustomValue', 'NFC value is required');
              }
            }}
          />
          {isCreate && (
            <GroupTagsSection
              isEdit
              tags={groupTags}
              focusedField={focusedField}
              onTagsChange={(tags) => dispatch(setGroupTags(tags))}
              onFieldFocus={onFieldFocus}
            />
          )}
        </Box>
      )}

      {/* View-only: stamp pass */}
      {isViewMode && isStampCard && uuid && totalStamps > 0 && (
        <StampPassSection
          passUuid={uuid}
          currentStamps={currentStamps}
          totalStamps={totalStamps}
        />
      )}

      {isViewMode && (
        <>
          <Box ref={walletSectionRef} borderRadius="10px">
            <DistributionLinkSection distributionUrl={distributionUrl} />
          </Box>
          <Box ref={notificationSectionRef} borderRadius="10px">
            <SendTestNotificationSection />
          </Box>
        </>
      )}

      {/* Group tags: editable on edit, read-only on view */}
      {isEdit ? (
        <GroupTagsSection
          isEdit
          entityId={templateEntityUuid}
          tags={groupTags}
          focusedField={focusedField}
          onTagsChange={(tags) => dispatch(setGroupTags(tags))}
          onFieldFocus={onFieldFocus}
        />
      ) : (
        isViewMode &&
        groupTags.length > 0 && <GroupTagsSection tags={groupTags} />
      )}

      {/* View-only: management and scanning */}
      {isViewMode && (
        <>
          <PassManagementSection attributes={pass?.attributes} />
          {(passHasNfc || pass?.attributes?.barcode?.enabled) && (
            <ScanningSection
              barcode={pass?.attributes?.barcode}
              nfcRaw={typeof nfcRaw === 'string' ? nfcRaw : null}
              passId={pass?.id}
              extId={pass?.attributes?.extId}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default PassBuilderPanel;
